import { useRef, useState } from "react";
import { Plus, Trash2, RefreshCw, Upload } from "lucide-react";
import {
  entitySchema,
  dateToCalendar,
  todayIn,
  type Entity,
  type EntityData,
  type Kind,
  type User,
} from "@kkindex/shared";
import { api } from "./api";
import { Modal } from "./components";
import s from "./App.module.css";
export type Execute = (
  path: string,
  method: string,
  body?: unknown,
) => Promise<unknown>;
export function Editor({
  item,
  kind,
  parentId,
  items,
  user,
  execute,
  onClose,
}: {
  item?: Entity;
  kind: Kind;
  parentId?: string;
  items: Entity[];
  user: User;
  execute: Execute;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<EntityData>(
    () =>
      item || {
        ...entitySchema.parse({
          title: "未命名",
          parentId: parentId || null,
          ...dateToCalendar(todayIn(user.timezone), "solar"),
        }),
        title: "",
      },
  );
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [linkUrl, setLinkUrl] = useState(""),
    [linkName, setLinkName] = useState(""),
    [imageUrl, setImageUrl] = useState("");
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  const current = items.find((x) => x.id === item?.id) || item;
  const set = (patch: Partial<EntityData>) =>
    setDraft((d) => ({ ...d, ...patch }));
  const persist = async (patch: Partial<EntityData>) => {
    set(patch);
    if (!item) return;
    setError("");
    try {
      await execute(`/items/${item.id}`, "PATCH", {
        ...patch,
        revision: current?.revision,
      });
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const text = (field: "title" | "note" | "url") => ({
    value: draft[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      set({ [field]: e.target.value }),
    onBlur: () => {
      if (current && draft[field] !== current[field])
        void persist({ [field]: draft[field] });
    },
  });
  const guarded = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  async function lookup() {
    await guarded(async () => {
      const url = draft.url;
      const r = await api<{
        title: string;
        assetId: string | null;
        warning: string;
      }>("/metadata", "POST", {
        url,
        icon: true,
        title: !latestDraft.current.title.trim(),
      });
      if (latestDraft.current.url !== url) return;
      const patch: Partial<EntityData> = {
        ...(!latestDraft.current.title.trim() ? { title: r.title } : {}),
        ...(r.assetId ? { assetId: r.assetId } : {}),
      };
      if (Object.keys(patch).length) await persist(patch);
      setError(r.warning);
    });
  }
  async function addLink() {
    await guarded(async () => {
      let name = linkName.trim();
      if (!name) {
        const r = await api<{ title: string }>("/metadata", "POST", {
          url: linkUrl,
          icon: false,
        });
        name = r.title;
      }
      const link = { name, url: linkUrl };
      const parsed = entitySchema.shape.links.parse([...draft.links, link]);
      await persist({ links: parsed });
      setLinkName("");
      setLinkUrl("");
    });
  }
  const dateFields = (
    <div className={s.two}>
      <label>
        开始日期
        <input
          type="date"
          value={draft.start || ""}
          onChange={(e) =>
            set({
              start: e.target.value || null,
              end: draft.end || e.target.value || null,
            })
          }
          onBlur={() =>
            void persist({
              start: draft.start,
              end: draft.start ? draft.end || draft.start : null,
            })
          }
        />
      </label>
      <label>
        结束日期
        <input
          type="date"
          value={draft.end || ""}
          min={draft.start || undefined}
          onChange={(e) => set({ end: e.target.value || null })}
          onBlur={() =>
            void persist({
              start: draft.start,
              end: draft.start ? draft.end || draft.start : null,
            })
          }
        />
      </label>
    </div>
  );
  return (
    <Modal
      title={`${item ? "编辑" : "添加"}${{ parent: "父任务", task: "子任务", event: "日历事项", group: "导航分组", site: "网站" }[kind]}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!item)
            void guarded(async () => {
              let values = draft;
              if (kind === "site") {
                const meta = await api<{
                  title: string;
                  assetId: string | null;
                  warning: string;
                }>("/metadata", "POST", {
                  url: draft.url,
                  icon: !draft.assetId,
                });
                values = {
                  ...draft,
                  title: draft.title.trim() || meta.title,
                  assetId: draft.assetId || meta.assetId,
                };
                setDraft(values);
              }
              const data = entitySchema.parse(values);
              if (
                kind === "site" &&
                items.some(
                  (x) =>
                    x.kind === "site" &&
                    x.parentId === data.parentId &&
                    x.url === data.url &&
                    !x.deletedAt,
                ) &&
                !confirm("该分组已有相同网址，仍然添加？")
              )
                return;
              await execute(`/items/${kind}`, "POST", data);
              onClose();
            });
        }}
      >
        <label>
          标题
          <input
            required={kind !== "site"}
            autoFocus
            maxLength={200}
            {...text("title")}
            disabled={item?.builtin}
          />
        </label>
        {(kind === "task" || kind === "site") && (
          <label>
            {kind === "task" ? "所属父任务" : "导航分组"}
            <select
              value={draft.parentId || ""}
              onChange={(e) => void persist({ parentId: e.target.value })}
            >
              {items
                .filter(
                  (x) =>
                    x.kind === (kind === "task" ? "parent" : "group") &&
                    !x.deletedAt &&
                    !x.archived,
                )
                .map((p) => (
                  <option value={p.id} key={p.id}>
                    {p.title}
                  </option>
                ))}
            </select>
          </label>
        )}
        {(kind === "parent" || kind === "event") && (
          <label>
            颜色
            <div className={s.colors}>
              {[
                "#5c82d9",
                "#579a86",
                "#b28a4e",
                "#9a76bf",
                "#c27682",
                "#718292",
              ].map((color) => (
                <button
                  type="button"
                  key={color}
                  style={{ background: color }}
                  aria-label={`颜色 ${color}`}
                  aria-pressed={draft.color === color}
                  onClick={() => void persist({ color })}
                />
              ))}
              <input
                aria-label="自定义颜色"
                type="color"
                value={draft.color}
                onChange={(e) => void persist({ color: e.target.value })}
              />
            </div>
          </label>
        )}
        {kind === "parent" && (
          <label>
            日期来源
            <select
              value={draft.dateMode}
              onChange={(e) =>
                void persist({ dateMode: e.target.value as "auto" | "manual" })
              }
            >
              <option value="auto">自动汇总子任务</option>
              <option value="manual">手动设置范围</option>
            </select>
          </label>
        )}
        {(kind === "task" ||
          (kind === "parent" && draft.dateMode === "manual")) && (
          <>
            {dateFields}
            <button
              type="button"
              className={s.subtle}
              onClick={() => void persist({ start: null, end: null })}
            >
              移除日期
            </button>
          </>
        )}
        {kind === "event" && (
          <>
            <div className={s.two}>
              <label>
                历法
                <select
                  value={draft.calendar}
                  onChange={(e) =>
                    void persist({
                      calendar: e.target.value as "solar" | "lunar",
                    })
                  }
                >
                  <option value="solar">公历</option>
                  <option value="lunar">农历</option>
                </select>
              </label>
              <label>
                重复
                <select
                  value={draft.repeat}
                  onChange={(e) =>
                    void persist({
                      repeat: e.target.value as "once" | "yearly",
                    })
                  }
                >
                  <option value="once">仅一次</option>
                  <option value="yearly">每年</option>
                </select>
              </label>
            </div>
            {draft.repeat === "yearly" && (
              <p className={s.hint}>
                修改日期和持续天数会应用到整个年度系列。缺失的日期取当月最后一天。
              </p>
            )}
            <div className={s.three}>
              {(["year", "month", "day"] as const).map((key, i) => (
                <label key={key}>
                  {["起始年份", "月", "日"][i]}
                  <input
                    type="number"
                    min={key === "year" ? 1900 : 1}
                    max={
                      key === "year"
                        ? 2100
                        : key === "month"
                          ? 12
                          : draft.calendar === "lunar"
                            ? 30
                            : 31
                    }
                    value={draft[key]}
                    onChange={(e) => set({ [key]: Number(e.target.value) })}
                    onBlur={() => void persist({ [key]: draft[key] })}
                  />
                </label>
              ))}
            </div>
            {draft.calendar === "lunar" && (
              <div className={s.two}>
                <label>
                  月份类型
                  <select
                    value={String(draft.leap)}
                    onChange={(e) =>
                      void persist({ leap: e.target.value === "true" })
                    }
                  >
                    <option value="false">普通月份</option>
                    <option value="true">闰月</option>
                  </select>
                </label>
                {draft.leap && (
                  <label>
                    当年没有闰月
                    <select
                      value={draft.leapPolicy}
                      onChange={(e) =>
                        void persist({
                          leapPolicy: e.target.value as "normal" | "skip",
                        })
                      }
                    >
                      <option value="normal">使用普通同月</option>
                      <option value="skip">当年跳过</option>
                    </select>
                  </label>
                )}
              </div>
            )}
            <div className={s.two}>
              <label>
                持续天数
                <input
                  type="number"
                  min={1}
                  max={366}
                  value={draft.duration}
                  onChange={(e) => set({ duration: Number(e.target.value) })}
                  onBlur={() => void persist({ duration: draft.duration })}
                />
              </label>
              <label>
                图标
                <select
                  value={draft.icon}
                  onChange={(e) => void persist({ icon: e.target.value })}
                >
                  <option value="calendar">日历</option>
                  <option value="cake">生日</option>
                  <option value="heart">纪念日</option>
                  <option value="plane">出行</option>
                  <option value="wallet">缴费</option>
                </select>
              </label>
            </div>
          </>
        )}
        {kind === "site" && (
          <>
            <label>
              网址
              <input
                required
                type="url"
                {...text("url")}
                placeholder="https://"
              />
            </label>
            <button
              type="button"
              className={s.outline}
              disabled={busy || !draft.url}
              onClick={() => void lookup()}
            >
              <RefreshCw size={14} />
              自动获取图标
            </button>
            <div className={s.iconPreview}>
              {draft.assetId ? (
                <img src={`/api/assets/${draft.assetId}`} alt="网站图标" />
              ) : (
                <span>{draft.title[0] || "K"}</span>
              )}
              <label className={s.upload}>
                <Upload size={14} />
                上传图标
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    void guarded(async () => {
                      if (file.size > 2 * 1024 * 1024)
                        throw new Error("图片不能超过 2MB");
                      const base64 = await new Promise<string>(
                        (resolve, reject) => {
                          const r = new FileReader();
                          r.onload = () =>
                            resolve(String(r.result).split(",")[1]);
                          r.onerror = reject;
                          r.readAsDataURL(file);
                        },
                      );
                      const result = await api<{ assetId: string }>(
                        "/assets",
                        "POST",
                        { base64 },
                      );
                      await persist(result);
                    });
                  }}
                />
              </label>
            </div>
            <div className={s.inline}>
              <input
                aria-label="图标图片地址"
                placeholder="或填写图片网址"
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
              <button
                type="button"
                disabled={busy || !imageUrl}
                onClick={() =>
                  void guarded(async () => {
                    const r = await api<{ assetId: string }>(
                      "/assets",
                      "POST",
                      { url: imageUrl },
                    );
                    await persist(r);
                  })
                }
              >
                获取
              </button>
            </div>
          </>
        )}
        {["parent", "task", "event"].includes(kind) && (
          <>
            <label>
              备注
              <textarea rows={3} {...text("note")} />
            </label>
            <label>相关链接</label>
            <div className={s.linkEditor}>
              {draft.links.map((l, i) => (
                <div className={s.inline} key={`${i}-${l.url}`}>
                  <a href={l.url} target="_blank" rel="noopener noreferrer">
                    {l.name || l.url}
                  </a>
                  <button
                    type="button"
                    aria-label={`删除链接 ${l.name}`}
                    onClick={() =>
                      void persist({
                        links: draft.links.filter((_, n) => n !== i),
                      })
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <input
              aria-label="链接名称"
              placeholder="链接名称（可自动获取）"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
            />
            <div className={s.inline}>
              <input
                aria-label="链接网址"
                type="url"
                placeholder="https://"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <button
                type="button"
                disabled={busy || !linkUrl}
                aria-label="添加链接"
                onClick={() => void addLink()}
              >
                <Plus size={17} />
              </button>
            </div>
          </>
        )}
        {error && (
          <p role="alert" className={s.error}>
            {error}
          </p>
        )}
        <footer>
          {item && !item.builtin && (
            <button
              type="button"
              className={s.danger}
              onClick={() =>
                void guarded(async () => {
                  let suffix = "";
                  if (kind === "group") {
                    const children = items.filter(
                      (x) => x.parentId === item.id && !x.deletedAt,
                    );
                    if (children.length) {
                      const target = prompt(
                        "输入目标分组名称可移动网站；输入“删除”连同网站一起删除",
                      );
                      if (target === null) return;
                      const group = items.find(
                        (x) =>
                          x.kind === "group" &&
                          x.id !== item.id &&
                          !x.deletedAt &&
                          x.title === target,
                      );
                      if (target === "删除") suffix = "?moveTo=";
                      else if (group) suffix = `?moveTo=${group.id}`;
                      else throw new Error("目标分组不存在");
                    }
                  }
                  const n = items.filter(
                    (x) => x.parentId === item.id && !x.deletedAt,
                  ).length;
                  if (
                    !confirm(
                      kind === "event" && draft.repeat === "yearly"
                        ? "删除整个年度事项系列？"
                        : `删除“${draft.title}”${n ? `及其 ${n} 个下属项目` : ""}？`,
                    )
                  )
                    return;
                  await execute(
                    `/items/${item.id}${suffix}${suffix ? "&" : "?"}revision=${current?.revision}`,
                    "DELETE",
                  );
                  onClose();
                })
              }
            >
              <Trash2 size={14} />
              删除
            </button>
          )}
          {item ? (
            <span className={s.hint}>修改后自动保存</span>
          ) : (
            <button disabled={busy} className={s.primary} type="submit">
              创建
            </button>
          )}
        </footer>
      </form>
    </Modal>
  );
}
